import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import type { StateAction } from "@entrilia/shared/types.ts";
import { checkAccess } from "@entrilia/shared/utilities/accessHelper.ts";
import type {
  GroupBasedAuthorization,
  GroupForSharing,
  ReportAuthorization,
  UserBasedAuthorization,
  UserForSharing,
} from "../../../../../../../../../api/types/svTypes.ts";

export interface ShareReportsBulkState {
  authorizedUsers: UserBasedAuthorization[];
  authorizedGroups: GroupBasedAuthorization[];
  companyAccess: ReportAccessType;
  inheritFromParent: boolean;
  hasEdits: boolean;
}

export const getInitialShareReportsBulkState = (inheritFromParent: boolean): ShareReportsBulkState => ({
  authorizedUsers: [],
  authorizedGroups: [],
  companyAccess: ReportAccessType.NoAccess,
  inheritFromParent,
  hasEdits: false,
});

export const addUsersAction =
  (users: UserForSharing[], access: ReportAccessType): StateAction<ShareReportsBulkState> =>
  (state) => ({
    ...state,
    hasEdits: true,
    authorizedUsers: [
      ...state.authorizedUsers,
      ...users.map((user): UserBasedAuthorization => ({ userId: user.userId, access })),
    ],
  });

export const addGroupsAction =
  (groups: GroupForSharing[], access: ReportAccessType): StateAction<ShareReportsBulkState> =>
  (state) => ({
    ...state,
    hasEdits: true,
    authorizedGroups: [
      ...state.authorizedGroups,
      ...groups.map((group): GroupBasedAuthorization => ({ groupId: group.groupId, access })),
    ],
  });

export const changeUserAccessAction =
  (userId: string, access: ReportAccessType): StateAction<ShareReportsBulkState> =>
  (state) => ({
    ...state,
    hasEdits: true,
    authorizedUsers: state.authorizedUsers.map((user) => (user.userId === userId ? { ...user, access } : user)),
  });

export const changeGroupAccessAction =
  (groupId: string, access: ReportAccessType): StateAction<ShareReportsBulkState> =>
  (state) => ({
    ...state,
    hasEdits: true,
    authorizedGroups: state.authorizedGroups.map((group) => (group.groupId === groupId ? { ...group, access } : group)),
  });

export const removeUserAction =
  (userId: string): StateAction<ShareReportsBulkState> =>
  (state) => ({
    ...state,
    hasEdits: true,
    authorizedUsers: state.authorizedUsers.filter((user) => user.userId !== userId),
  });

export const removeGroupAction =
  (groupId: string): StateAction<ShareReportsBulkState> =>
  (state) => ({
    ...state,
    hasEdits: true,
    authorizedGroups: state.authorizedGroups.filter((group) => group.groupId !== groupId),
  });

export const setCompanyAccessAction =
  (companyAccess: ReportAccessType): StateAction<ShareReportsBulkState> =>
  (state) => ({ ...state, companyAccess, hasEdits: true });

export const setInheritFromParentAction =
  (inheritFromParent: boolean): StateAction<ShareReportsBulkState> =>
  (state) => ({ ...state, inheritFromParent, hasEdits: true });

export const isSharingChanged = (state: ShareReportsBulkState, defaultInheritFromParent: boolean): boolean => {
  if (!state.hasEdits) {
    return false;
  }
  return !state.inheritFromParent || !defaultInheritFromParent;
};

export const willLoseOwnAccess = (
  state: ShareReportsBulkState,
  currentUserId: string,
  ownsEveryReport: boolean
): boolean => {
  if (state.inheritFromParent || ownsEveryReport) {
    return false;
  }
  const ownAccess =
    state.authorizedUsers.find((user) => user.userId === currentUserId)?.access ?? ReportAccessType.NoAccess;
  const maximumAccess = ownAccess > state.companyAccess ? ownAccess : state.companyAccess;
  return !checkAccess(maximumAccess, ReportAccessType.FullAccess);
};

export const getAuthorizationToApply = (state: ShareReportsBulkState): ReportAuthorization | undefined => {
  if (state.inheritFromParent) {
    return undefined;
  }
  return {
    users: state.authorizedUsers,
    groups: state.authorizedGroups,
    companyShared: { access: state.companyAccess },
  };
};
